import { supabase } from '../supabaseClient';
import { getImpersonatedUserIdFromStorage } from '../../contexts/ImpersonationContext';
import { getCategories, Categorie } from './CategoryService';
import { getComptes } from './AccountService';
import {
  BankId,
  detectAndParse,
  ParsedImport,
  ParsedRow,
} from '../import/bankParsers';

export interface ImportError {
  line: number;
  message: string;
  data?: unknown;
}

export interface ImportResult {
  success: boolean;
  totalLines: number;
  importedCount: number;
  duplicatesSkipped: number;
  errors: ImportError[];
}

export interface ImportPreviewRow {
  key: string;
  date: string;
  description: string;
  montant: number;
  type: 'depense' | 'revenu' | 'transfert';
  categorieNom: string;
  compteNom: string;
  compteDestinationNom: string;
  rawLine?: number;
  warning?: string;
  error?: string;
  skip: boolean;
  /** Présent en base (même date + même montant) — coché à ignorer après vérif */
  duplicateDb?: boolean;
}

export interface DuplicateCheckResult {
  rows: ImportPreviewRow[];
  matched: number;
  checked: number;
  error?: string;
}

export interface PrepareImportResult {
  parsed: ParsedImport;
  rows: ImportPreviewRow[];
}

const BATCH_SIZE = 200;
const HASH_KEY_PREFIX = 'import_hashes_';

function makeRowKey(row: ParsedRow, index: number): string {
  return `${row.rawLine ?? index}-${row.date}-${row.montant}-${index}`;
}

function signedMontant(type: ImportPreviewRow['type'], raw: number): number {
  const abs = Math.abs(raw);
  if (type === 'revenu') return abs;
  return -abs;
}

function importHash(
  date: string,
  montant: number,
  description: string,
  compteId: string,
): string {
  return `${date}_${montant}_${(description || '').slice(0, 30)}_${compteId}`;
}

function loadImportHashes(userId: string): Set<string> {
  try {
    const raw = localStorage.getItem(`${HASH_KEY_PREFIX}${userId}`);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveImportHashes(userId: string, hashes: Set<string>): void {
  const arr = Array.from(hashes);
  const trimmed = arr.length > 5000 ? arr.slice(arr.length - 5000) : arr;
  localStorage.setItem(`${HASH_KEY_PREFIX}${userId}`, JSON.stringify(trimmed));
}

function matchCategorie(
  categories: Categorie[],
  nom: string,
  type: ImportPreviewRow['type'],
): Categorie | undefined {
  if (!nom?.trim()) return undefined;
  const needle = nom.trim().toLowerCase();
  const typeFilter = type === 'transfert' ? 'depense' : type;
  return (
    categories.find((c) => c.nom.toLowerCase() === needle && c.type === typeFilter) ||
    categories.find((c) => c.nom.toLowerCase() === needle) ||
    categories.find(
      (c) => c.nom.toLowerCase().includes(needle) && c.type === typeFilter,
    )
  );
}

export function prepareImportFromCSV(
  csvContent: string,
  forcedBank?: BankId | 'auto',
): PrepareImportResult {
  const parsed = detectAndParse(csvContent, { forcedBank });
  const rows: ImportPreviewRow[] = parsed.rows.map((row, index) => {
    const type = row.type;
    const montant = signedMontant(type, row.montant);
    return {
      key: makeRowKey(row, index),
      date: row.date,
      description: row.description,
      montant,
      type,
      categorieNom: row.categorieHint || '',
      compteNom: row.compteHint || '',
      compteDestinationNom: row.compteDestinationHint || '',
      rawLine: row.rawLine,
      warning: row.warning,
      skip: false,
      duplicateDb: false,
    };
  });
  return { parsed, rows };
}

function montantKey(date: string, montant: number): string {
  return `${date}_${Number(montant).toFixed(2)}`;
}

/**
 * Compare la prévisualisation aux transactions déjà en base (date + montant).
 * Les lignes trouvées sont marquées duplicateDb + skip.
 */
export async function checkPreviewDuplicatesInDb(
  previewRows: ImportPreviewRow[],
): Promise<DuplicateCheckResult> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      return {
        rows: previewRows,
        matched: 0,
        checked: 0,
        error: 'Utilisateur non connecté',
      };
    }

    const candidates = previewRows.filter(
      (r) => !r.error && !!r.date && Number.isFinite(r.montant),
    );
    if (candidates.length === 0) {
      return { rows: previewRows, matched: 0, checked: 0 };
    }

    const dates = candidates.map((r) => r.date).sort();
    const from = dates[0]!;
    const to = dates[dates.length - 1]!;

    let query = supabase
      .from('transactions')
      .select('date, montant')
      .gte('date', from)
      .lte('date', to);

    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    if (impersonatedUserId) {
      query = query.eq('user_id', impersonatedUserId);
    } else {
      query = query.eq('user_id', user.id);
    }

    const { data, error } = await query;
    if (error) {
      return {
        rows: previewRows,
        matched: 0,
        checked: candidates.length,
        error: error.message,
      };
    }

    const pool = new Map<string, number>();
    for (const t of data || []) {
      const key = montantKey(String(t.date).slice(0, 10), Number(t.montant));
      pool.set(key, (pool.get(key) || 0) + 1);
    }

    let matched = 0;
    const rows = previewRows.map((r) => {
      if (r.error || !r.date || !Number.isFinite(r.montant)) {
        return { ...r, duplicateDb: false };
      }
      const key = montantKey(r.date, r.montant);
      const left = pool.get(key) || 0;
      if (left > 0) {
        pool.set(key, left - 1);
        matched += 1;
        return {
          ...r,
          duplicateDb: true,
          skip: true,
          warning: r.warning?.includes('Déjà en base')
            ? r.warning
            : r.warning
              ? `${r.warning} · Déjà en base (date + montant)`
              : 'Déjà en base (date + montant)',
        };
      }
      // Ne pas effacer un skip manuel ; retirer seulement le marquage DB précédent
      if (r.duplicateDb) {
        return {
          ...r,
          duplicateDb: false,
          skip: false,
          warning: r.warning?.includes('Déjà en base')
            ? r.warning
                .replace(/\s*·\s*Déjà en base \(date \+ montant\)/g, '')
                .replace(/Déjà en base \(date \+ montant\)/g, '')
                .trim() || undefined
            : r.warning,
        };
      }
      return { ...r, duplicateDb: false };
    });

    return { rows, matched, checked: candidates.length };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Erreur inconnue';
    return {
      rows: previewRows,
      matched: 0,
      checked: 0,
      error: message,
    };
  }
}

export const importPreviewRows = async (
  previewRows: ImportPreviewRow[],
): Promise<ImportResult> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    if (!user) {
      return {
        success: false,
        totalLines: 0,
        importedCount: 0,
        duplicatesSkipped: 0,
        errors: [{ line: 0, message: 'Utilisateur non connecté' }],
      };
    }
    const userId = impersonatedUserId || user.id;

    const { data: categories } = await getCategories();
    const { data: comptes } = await getComptes();

    if (!categories || !comptes) {
      return {
        success: false,
        totalLines: previewRows.length,
        importedCount: 0,
        duplicatesSkipped: 0,
        errors: [
          {
            line: 0,
            message: 'Impossible de récupérer les catégories ou les comptes',
          },
        ],
      };
    }

    const result: ImportResult = {
      success: true,
      totalLines: previewRows.length,
      importedCount: 0,
      duplicatesSkipped: 0,
      errors: [],
    };

    const hashes = loadImportHashes(userId);
    const defaultCompte =
      comptes.find((c) => c.is_default) || comptes.find((c) => c.actif !== false) || comptes[0];
    const toInsert: Array<Record<string, unknown>> = [];

    for (const row of previewRows) {
      const line = row.rawLine ?? 0;
      if (row.skip || row.error) continue;

      if (!row.date || !row.description?.trim()) {
        result.errors.push({ line, message: 'Date ou description manquante', data: row });
        continue;
      }
      if (!Number.isFinite(row.montant) || row.montant === 0) {
        result.errors.push({
          line,
          message: `Montant invalide: ${row.montant}`,
          data: row,
        });
        continue;
      }

      const type = row.type;
      const montant = signedMontant(type, row.montant);

      let categorie = matchCategorie(categories, row.categorieNom, type);
      if (!categorie) {
        categorie = categories.find(
          (c) => c.type === (type === 'transfert' ? 'depense' : type),
        );
      }
      if (!categorie) {
        result.errors.push({
          line,
          message: `Aucune catégorie valide pour le type ${type}`,
        });
        continue;
      }

      let compteId = defaultCompte?.id;
      if (row.compteNom?.trim()) {
        const match = comptes.find(
          (c) => c.nom.toLowerCase() === row.compteNom.trim().toLowerCase(),
        );
        if (match) compteId = match.id;
        else {
          result.errors.push({
            line,
            message: `Compte non trouvé: ${row.compteNom}`,
          });
          continue;
        }
      }
      if (!compteId) {
        result.errors.push({ line, message: 'Aucun compte valide trouvé' });
        continue;
      }

      let compteDestinationId: string | undefined;
      if (type === 'transfert') {
        if (!row.compteDestinationNom?.trim()) {
          result.errors.push({
            line,
            message: 'Compte destination requis pour un transfert',
          });
          continue;
        }
        const dest = comptes.find(
          (c) =>
            c.nom.toLowerCase() === row.compteDestinationNom.trim().toLowerCase(),
        );
        if (!dest) {
          result.errors.push({
            line,
            message: `Compte de destination non trouvé: ${row.compteDestinationNom}`,
          });
          continue;
        }
        if (dest.id === compteId) {
          result.errors.push({
            line,
            message: 'Comptes source et destination identiques',
          });
          continue;
        }
        compteDestinationId = dest.id;
      }

      const hash = importHash(row.date, montant, row.description, compteId);
      if (hashes.has(hash)) {
        result.duplicatesSkipped++;
        continue;
      }

      const payload: Record<string, unknown> = {
        user_id: userId,
        date: row.date,
        description: row.description.trim(),
        montant,
        type,
        categorie_id: categorie.id,
        compte_id: compteId,
      };
      if (compteDestinationId) payload.compte_destination_id = compteDestinationId;

      toInsert.push(payload);
      hashes.add(hash);
    }

    for (let i = 0; i < toInsert.length; i += BATCH_SIZE) {
      const batch = toInsert.slice(i, i + BATCH_SIZE);
      const { error } = await supabase.from('transactions').insert(batch);
      if (error) {
        result.errors.push({
          line: 0,
          message: `Erreur batch insert (${i}-${i + batch.length}): ${error.message}`,
          data: error,
        });
      } else {
        result.importedCount += batch.length;
      }
    }

    if (result.importedCount > 0) {
      saveImportHashes(userId, hashes);
    }

    result.success = result.importedCount > 0;
    return result;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Erreur inconnue';
    console.error('Exception dans importPreviewRows:', error);
    return {
      success: false,
      totalLines: 0,
      importedCount: 0,
      duplicatesSkipped: 0,
      errors: [{ line: 0, message: `Erreur générale: ${message}` }],
    };
  }
};
